import type { ClientEngineConfiguration } from "./ClientEngineConfiguration";

import type { SpinRequest } from "./round/contracts/SpinRequest";
import type {
  SpinResponse,
  RejectedSpinResponse,
  SpinResult,
} from "./round/contracts/SpinResponse";
import type { PresentationCompleteRequest, PresentationCompleteResponse } from "./round/contracts/PresentationCompleteRequest";
import type {
  GambleRequest,
  GambleResponse,
  GambleCompleteRequest,
  GambleCompleteResponse,
} from "./round/contracts/GambleContracts";

import { validateSpinResponseOrThrow } from "./round/validation/SpinResponseValidator";
import { validateRoundSnapshotOrThrow } from "./round/validation/RoundSnapshotValidator";

import { validateFeatureTriggerConsistencyOrThrow } from "./round/validation/FeatureTriggerConsistencyValidator";

import type { ReplayConfiguration } from "./round/replay/core/ReplayConfiguration";
import type { ReplayPlugin } from "./round/replay/plugins/ReplayPlugin";
import type { EventObserver, FramePresenter } from "./round/replay/plugins/FramePresenter";
import { createDefaultRoundReplayEngine } from "./round/replay/core/RoundReplayEngineFactory";
import type { RoundReplayEngine } from "./round/replay/core/RoundReplayEngine";

import { extractLastNonEmptyAccumulatedWinAmount } from "./round/validation/RoundWinExtraction";

/**
 * The engine over a round provider: requests a round, validates the contract, replays it through
 * the frame presenter and the plugins (in list order), settles it once per round, and carries the
 * gamble calls to the provider. Pure protocol: no rendering, no wallet.
 */
export type RequestPlayRejectedResult = {
  kind: "rejected";
  rejectedResponse: RejectedSpinResponse;
};

export type RequestPlayOkResult = {
  kind: "ok";
  spinResponse: SpinResponse;
  presentationPromise: Promise<void>;
  settlementPromise?: Promise<PresentationCompleteResponse>;
};

export type SpinThenPresentThenSettleResult =
  | RequestPlayRejectedResult
  | RequestPlayOkResult;

function isReplayableSpinResponse(response: SpinResult): response is SpinResponse {
  return !!(response as SpinResponse).roundSnapshot;
}

export class ClientEngine {
  private readonly roundProvider: ClientEngineConfiguration["roundProvider"];
  private readonly roundReplayEngine: RoundReplayEngine;
  private readonly logDebugMessage?: (message: string) => void;
  private readonly framePresenter: FramePresenter;
  private readonly observers: readonly EventObserver[];
  private readonly enableFeatureTriggerConsistencyValidation: boolean;

  /**
   * Cache of in-flight (or resolved) settlement acks keyed by roundIdentifier.
   *
   * `acknowledgePresentationCompleteOnce` reads/writes this map so that the
   * first caller fires the HTTP request and subsequent callers (for the same
   * round) join the same promise — no duplicate POST. The presentation layer
   * can therefore trigger settlement early (e.g. when a feature-finished
   * panel becomes visible) while the engine's end-of-replay safety net still
   * tries to settle if it hasn't already.
   *
   * On rejection the entry is evicted so a retry can fire fresh.
   */
  private readonly settlementsByRoundId = new Map<string, Promise<PresentationCompleteResponse>>();

  public constructor(configuration: ClientEngineConfiguration) {
    this.roundProvider = configuration.roundProvider;
    const plugins: readonly ReplayPlugin[] = configuration.plugins ?? [];
    this.framePresenter = configuration.framePresenter;
    this.observers = plugins.flatMap((plugin) => (plugin.observer ? [plugin.observer] : []));
    this.roundReplayEngine = createDefaultRoundReplayEngine({
      actionHandlers: [...plugins.flatMap((plugin) => plugin.actionHandlers ?? []), ...(configuration.additionalActionHandlers ?? [])],
      eventHandlers: [...plugins.flatMap((plugin) => plugin.eventHandlers ?? []), ...(configuration.additionalEventHandlers ?? [])],
    });
    this.logDebugMessage = configuration.logDebugMessage;

    this.enableFeatureTriggerConsistencyValidation =
      configuration.enableFeatureTriggerConsistencyValidation ?? false;

  }

  /** The provider's answer: a replayable round (validated against the contract) or a server-authoritative rejection. */
  private async fetchSpinResult(spinRequest: SpinRequest): Promise<SpinResult> {
    const spinResponse = await this.roundProvider.requestRoundSnapshot(spinRequest);
    if (isReplayableSpinResponse(spinResponse)) {
      validateSpinResponseOrThrow(spinResponse);
      validateRoundSnapshotOrThrow(spinResponse.roundSnapshot);
      if (this.enableFeatureTriggerConsistencyValidation) validateFeatureTriggerConsistencyOrThrow(spinResponse.roundSnapshot);
    }
    return spinResponse;
  }

  /**
   * Deterministically replays the round inside a successful spin response.
   */
  public async playSpinResponse(
    spinResponse: SpinResponse,
    replayConfiguration?: Partial<ReplayConfiguration>
  ): Promise<void> {
    validateSpinResponseOrThrow(spinResponse);
    validateRoundSnapshotOrThrow(spinResponse.roundSnapshot);

    void (await this.roundReplayEngine.playRoundSnapshot(
      spinResponse.roundSnapshot,
      this.framePresenter,
      this.observers,
      replayConfiguration,
      this.logDebugMessage
    ));
  }

  /**
   * Requests a round and, when it is replayable, starts its presentation; a winning round settles
   * itself once the presentation completes (through the idempotent acknowledgement, so an early
   * settlement by the presentation layer joins the same request).
   */
  public async requestPlay(
    spinRequest: SpinRequest,
    replayConfiguration?: Partial<ReplayConfiguration>
  ): Promise<SpinThenPresentThenSettleResult> {
    const spinResponse = await this.fetchSpinResult(spinRequest);

    if (!isReplayableSpinResponse(spinResponse)) {
      return {
        kind: "rejected",
        rejectedResponse: spinResponse,
      };
    }

    // The round's total: its last event's accumulated win, which the validator requires (decision 26).
    const snapshotAccumulatedWinAmount =
      extractLastNonEmptyAccumulatedWinAmount(spinResponse.roundSnapshot) ?? "0";

    // Coerced BEFORE the replay starts. A money string the snapshot validator let through in a
    // shape BigInt cannot read must reject the request here, not throw from under a presentation
    // that is already driving the grid while the coordinator has moved on to its failure path.
    const snapshotWinIsPositive = BigInt(snapshotAccumulatedWinAmount) > 0n;

    // The replay's total is not compared with the snapshot's: the replay records each event's
    // accumulated win at the event's end, so after the last event the two are the same figure. Until
    // review 2026-09-27 #35 a `ReplayWinConsistencyError` compared them; it became unreachable once
    // the last event had to carry the total.
    const presentationPromise = this.roundReplayEngine
      .playRoundSnapshot(
        spinResponse.roundSnapshot,
        this.framePresenter,
      this.observers,
        replayConfiguration,
        this.logDebugMessage
      )
      .then(() => undefined);

    let settlementPromise: Promise<PresentationCompleteResponse> | undefined;

    if (snapshotWinIsPositive) {
      settlementPromise = (async () => {
        await presentationPromise;

        const roundIdentifier = spinResponse.roundSnapshot.roundIdentifier;
        // Routes through the idempotent cache. If the presentation layer
        // already triggered an early ack (e.g. feature-finished panel
        // appeared), this joins that same in-flight promise instead of
        // sending a second POST.
        return this.acknowledgePresentationCompleteOnce({ roundIdentifier });
      })();
      // The caller awaits `presentationPromise` first and never reaches `settlementPromise` when
      // that rejects, so this derived promise would reject unobserved and reach the window's
      // unhandledrejection listener — the boot boundary — ahead of the coordinator's own failure
      // modal, spending the boundary on a failure that is already being handled. The caller's
      // `await settlementPromise` still rejects; only the unhandled flag is cleared.
      settlementPromise.catch(() => {});
    }

    return {
      kind: "ok",
      spinResponse,
      presentationPromise,
      settlementPromise,
    };
  }

  /**
   * The engine's one settlement door, idempotent per round: the first caller for a
   * `roundIdentifier` sends the provider's `acknowledgePresentationComplete`; later callers for
   * the same round join the cached in-flight promise and receive the same response. A round can be
   * settled from more than one place, and two confirms for one round at once are the one case the
   * backend answers with a 500 rather than its idempotent 200, so the engine has no uncached door
   * (decision 35). Eviction on rejection lets a later caller retry if the first attempt failed —
   * the one settlement retry decision 17 allows (amended 2026-09-23), safe because the backend
   * credits a round once and answers a repeat with a 200.
   *
   * Used by:
   * - The engine's `requestPlay` path (the end-of-replay safety net).
   * - The presentation layer when a feature-finished panel becomes visible
   *   — settles the round while the player is still deciding what to do
   *   with the offer, shortening the wait for the next spin/buy.
   * - The pending-round restore (`GameRuntime.settlePendingRound`): a restored winning feature
   *   round has already sent the panel's early settlement, and this joins it.
   */
  public acknowledgePresentationCompleteOnce(
    request: PresentationCompleteRequest
  ): Promise<PresentationCompleteResponse> {
    const cached = this.settlementsByRoundId.get(request.roundIdentifier);
    if (cached) return cached;

    const promise = this.roundProvider.acknowledgePresentationComplete(request);

    // Cache the in-flight promise BEFORE any await, so concurrent callers
    // synchronously see the same promise and de-dup.
    this.settlementsByRoundId.set(request.roundIdentifier, promise);

    // Evict on rejection so a subsequent caller (e.g. the engine's
    // end-of-replay safety net) can retry instead of joining a poisoned
    // promise. Successful entries stay cached for the lifetime of the
    // engine — duplicate calls return the resolved response with no I/O.
    promise.catch(() => {
      this.settlementsByRoundId.delete(request.roundIdentifier);
    });

    return promise;
  }

  /**
   * Submit a gamble decision for the current round.
   * Requires the configured RoundProvider to support gamble.
   */
  public async requestGamble(request: GambleRequest): Promise<GambleResponse> {
    if (!this.roundProvider.requestGamble) {
      throw new Error("ClientEngine: gamble is not supported by this round provider");
    }
    return this.roundProvider.requestGamble(request);
  }

  /**
   * Acknowledge the end of the gamble flow. Returns the server's
   * authoritative post-gamble balance. The gamble phase's counterpart of
   * `acknowledgePresentationCompleteOnce`, without its cache: a gamble
   * session sends it once, at whichever exit ends the flow.
   * Requires the configured RoundProvider to support gamble settlement.
   */
  public async acknowledgeGambleComplete(
    request: GambleCompleteRequest
  ): Promise<GambleCompleteResponse> {
    if (!this.roundProvider.acknowledgeGambleComplete) {
      throw new Error(
        "ClientEngine: gamble settlement is not supported by this round provider"
      );
    }
    return this.roundProvider.acknowledgeGambleComplete(request);
  }

}